import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { getChannels, deleteChannel, getUnits, getChannelUnits } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Share2, 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Eye,
  EyeOff,
  Loader2,
  Search,
  CheckCircle2,
  Home
} from 'lucide-react';
import { toast } from 'sonner';
import { ChannelFormDialog } from './channels/ChannelFormDialog';
import { AllocateUnitsDialog } from './channels/AllocateUnitsDialog';

const getChannelTypeColor = (type) => {
  switch (type) {
    case 'internal': return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400';
    case 'partner': return 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400';
    case 'public': return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400';
    default: return 'bg-muted text-muted-foreground';
  }
};

const ChannelsPage = () => {
  const { user } = useAuth();
  const { isReadOnly } = usePlan();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);

  // Allocate dialog state
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [allUnits, setAllUnits] = useState([]);
  const [initialSelectedUnitIds, setInitialSelectedUnitIds] = useState([]);

  const loadChannels = async () => {
    try {
      const data = await getChannels();
      setChannels(data);
    } catch (error) {
      console.error('Failed to load channels:', error);
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChannels();
  }, []);

  const handleOpenDialog = (channel = null) => {
    setEditingChannel(channel || null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (channelId) => {
    if (!window.confirm('Are you sure you want to delete this channel?')) return;
    try {
      await deleteChannel(channelId);
      toast.success('Channel deleted successfully');
      loadChannels();
    } catch (error) {
      console.error('Failed to delete channel:', error);
      toast.error(error.message || 'Failed to delete channel');
    }
  };

  const openAllocateDialog = async (channel) => {
    setSelectedChannel(channel);
    setLoading(true);
    try {
      const [units, allocatedUnits] = await Promise.all([
        getUnits({}),
        getChannelUnits(channel.channel_id)
      ]);
      setAllUnits(units);
      setInitialSelectedUnitIds(allocatedUnits.map(u => u.unit_id));
      setIsAllocateDialogOpen(true);
    } catch (error) {
      console.error('Failed to load units:', error);
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const filteredChannels = channels.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && channels.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="channels-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Channels</h1>
          <p className="text-muted-foreground mt-1">
            Manage sales channels and inventory visibility
          </p>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'management') && !isReadOnly && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-channel-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Channel
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-channels"
        />
      </div>

      {/* Channels Grid */}
      {filteredChannels.length === 0 ? (
        <Card className="p-12 text-center">
          <Share2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No channels found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try a different search term' : 'Create your first channel to manage inventory access'}
          </p>
          {!searchQuery && (user?.role === 'admin' || user?.role === 'management') && (
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Channel
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredChannels.map((channel) => (
            <Card 
              key={channel.channel_id} 
              className="hover-lift hover-glow"
              data-testid={`channel-card-${channel.channel_id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Share2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{channel.name}</CardTitle>
                      <Badge variant="outline" className="mt-1">{channel.code}</Badge>
                    </div>
                  </div>
                  {(user?.role === 'admin' || user?.role === 'management') && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(channel)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAllocateDialog(channel)}>
                          <Home className="w-4 h-4 mr-2" />
                          Allocate Units
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(channel.channel_id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Badge className={getChannelTypeColor(channel.channel_type)}>
                    {channel.channel_type === 'internal' ? 'Internal' : 
                     channel.channel_type === 'partner' ? 'Partner' : 'Public'}
                  </Badge>
                  
                  {channel.description && (
                    <p className="text-sm text-muted-foreground">{channel.description}</p>
                  )}
                  
                  {/* Visibility Rules */}
                  <div className="flex flex-wrap gap-2">
                    {channel.visibility?.show_unit_numbers ? (
                      <Badge variant="secondary" className="text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        Unit Numbers
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hidden Units
                      </Badge>
                    )}
                    {channel.visibility?.show_pricing ? (
                      <Badge variant="secondary" className="text-xs">
                        <Eye className="w-3 h-3 mr-1" />
                        Pricing
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hidden Pricing
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      {channel.visibility?.allow_soft_block ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      Can Block
                    </div>
                    <div className="flex items-center gap-1">
                      {channel.visibility?.allow_booking ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      Can Book
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Channel Form Dialog */}
      <ChannelFormDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingChannel={editingChannel}
        developerId={user?.developer_id || 'dev_default'}
        onSaved={loadChannels}
      />

      {/* Allocate Units Dialog */}
      <AllocateUnitsDialog
        isOpen={isAllocateDialogOpen}
        onOpenChange={setIsAllocateDialogOpen}
        selectedChannel={selectedChannel}
        allUnits={allUnits}
        initialSelectedUnitIds={initialSelectedUnitIds}
        onAllocated={() => {}}
      />
    </div>
  );
};

export default ChannelsPage;
